import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UserCommunity {
  id: string;
  community_id: string;
  joined_at: string;
  is_active: boolean;
  community: {
    id: string;
    name: string;
    description?: string;
    members_count: number;
    plant_hardiness_zone?: string;
  };
}

export const useUserCommunities = (profileId?: string) => {
  const [userCommunities, setUserCommunities] = useState<UserCommunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchUserCommunities = async () => {
    if (!profileId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_communities')
        .select(`
          *,
          community:communities(*)
        `)
        .eq('profile_id', profileId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching user communities:', error);
        setError(error.message);
        return;
      }

      setUserCommunities(data || []);
      setError(null);
    } catch (err) {
      console.error('Unexpected error fetching user communities:', err);
      setError('Failed to fetch communities');
    } finally {
      setLoading(false);
    }
  };

  const joinCommunity = async (communityId: string) => {
    if (!profileId) return;

    try {
      const { error } = await supabase
        .from('user_communities')
        .insert({
          profile_id: profileId,
          community_id: communityId,
        });

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Already joined",
            description: "You're already a member of this community.",
            variant: "default",
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to join community. Please try again.",
            variant: "destructive",
          });
        }
        return false;
      }

      toast({
        title: "Success",
        description: "You've joined the community!",
      });
      
      await fetchUserCommunities();
      return true;
    } catch (err) {
      console.error('Error joining community:', err);
      toast({
        title: "Error",
        description: "Failed to join community. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const leaveCommunity = async (communityId: string) => {
    if (!profileId) return;

    try {
      const { error } = await supabase
        .from('user_communities')
        .delete()
        .eq('profile_id', profileId)
        .eq('community_id', communityId);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to leave community. Please try again.",
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Success",
        description: "You've left the community.",
      });
      
      await fetchUserCommunities();
      return true;
    } catch (err) {
      console.error('Error leaving community:', err);
      toast({
        title: "Error",
        description: "Failed to leave community. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchUserCommunities();
  }, [profileId]);

  return {
    userCommunities,
    loading,
    error,
    joinCommunity,
    leaveCommunity,
    refetch: fetchUserCommunities,
  };
};